// Phase P-LINK-EDU-TRIPLE
// Forward edges from glossary / guides / blog pages into the triple-key
// translation matrix /[en/]translate/$language/$doctype/in/$location.
//
// Educational pages attract long-tail organic + LLM citations; surfacing a
// curated set of money-page deep links closes the journey from "learning"
// to "ordering" and pushes PageRank into ~5,000 commercial combos.

import { Link } from "@tanstack/react-router";
import { ArrowRight, BookOpen } from "lucide-react";
import { getTranslationDocType } from "@/data/translation-doctypes";

const DEFAULT_DOCTYPES = [
  "passport",
  "marriage-certificate",
  "birth-certificate",
  "diploma",
  "transcript",
  "police-clearance",
  "house-registration",
  "id-card",
  "divorce-certificate",
  "medical-certificate",
];

const HUBS = [
  "bangkok-hq",
  "hub-sathorn-cbd",
  "hub-asoke-corridor",
  "hub-phuket-patong",
  "hub-chiangmai-nimman",
  "hub-pattaya",
] as const;

const HUB_LABEL_TH: Record<string, string> = {
  "bangkok-hq": "สำนักงานใหญ่ กรุงเทพฯ",
  "hub-asoke-corridor": "อโศก / สุขุมวิท",
  "hub-sathorn-cbd": "สาทร CBD",
  "hub-phuket-patong": "ภูเก็ต ป่าตอง",
  "hub-chiangmai-nimman": "เชียงใหม่ นิมมาน",
  "hub-pattaya": "พัทยา",
};
const HUB_LABEL_EN: Record<string, string> = {
  "bangkok-hq": "Bangkok HQ",
  "hub-asoke-corridor": "Asoke / Sukhumvit",
  "hub-sathorn-cbd": "Sathorn CBD",
  "hub-phuket-patong": "Phuket / Patong",
  "hub-chiangmai-nimman": "Chiang Mai / Nimman",
  "hub-pattaya": "Pattaya",
};

interface Props {
  mode: "th" | "en";
  /** Optional ordered doctype slugs to prioritise (e.g. tied to a blog topic). */
  doctypes?: string[];
  /** Heading override; falls back to a generic phrasing. */
  headingTh?: string;
  headingEn?: string;
}

export default function EducationalTripleKeyLinks({
  mode,
  doctypes,
  headingTh,
  headingEn,
}: Props) {
  const isTh = mode === "th";
  const list = doctypes && doctypes.length > 0 ? doctypes : DEFAULT_DOCTYPES;

  type Card = { slug: string; d: NonNullable<ReturnType<typeof getTranslationDocType>>; hub: string };
  const cards: Card[] = list
    .map((slug, i): Card | null => {
      const d = getTranslationDocType(slug);
      if (!d) return null;
      return { slug, d, hub: HUBS[i % HUBS.length] as string };
    })
    .filter((x): x is Card => x !== null)
    .slice(0, 6);

  if (cards.length === 0) return null;

  return (
    <section className="mt-14 border-t border-slate-200 pt-10">
      <div className="text-[11px] font-bold uppercase tracking-widest text-teal-700">
        {isTh ? "พร้อมแปลเอกสารแล้ว?" : "Ready to translate?"}
      </div>
      <h2 className="mt-1 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
        {isTh
          ? headingTh ?? "เอกสารยอดนิยมที่ลูกค้าสั่งแปลมากที่สุด"
          : headingEn ?? "Most-ordered certified translations"}
      </h2>
      <p className="mt-2 max-w-3xl text-sm text-slate-600">
        {isTh
          ? "เลือกเอกสารและฮับที่ใกล้คุณ — ราคา, คิวรับงาน, เวลาส่งมอบ และเอกสารตัวอย่าง ครบในหน้าเดียว"
          : "Pick the document and the hub closest to you — prices, queues, turnaround and sample translations all on one page."}
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map(({ slug, d, hub }) => (
          <Link
            key={`${slug}-${hub}`}
            to={isTh
              ? "/translate/$language/$doctype/in/$location"
              : "/en/translate/$language/$doctype/in/$location"}
            params={{ language: "english", doctype: slug, location: hub }}
            className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-teal-400 hover:shadow-md"
            data-track-zone="edu-triple-deep"
          >
            <BookOpen className="mt-0.5 h-5 w-5 shrink-0 text-teal-600" />
            <div className="flex-1">
              <div className="text-[10px] font-bold uppercase tracking-widest text-teal-700">
                {isTh ? d.nameTh : d.nameEn}
              </div>
              <div className="mt-0.5 font-display text-sm font-extrabold text-navy-deep group-hover:text-teal-700">
                {isTh ? `แปล ${d.nameTh} เป็นภาษาอังกฤษ` : `${d.nameEn} → English`}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                {isTh ? `รับ/นัดที่ ${HUB_LABEL_TH[hub] ?? hub}` : `Pick-up at ${HUB_LABEL_EN[hub] ?? hub}`}
              </div>
              <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-teal-700 group-hover:gap-1.5">
                {isTh ? "ดูราคา + ขั้นตอน" : "See price + steps"} <ArrowRight className="h-3 w-3" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
